import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@/store';
import { inventoryApi } from '@/services/api';
import Button from '@/components/ui/Button';
import toast from 'react-hot-toast';
import { getPrimaryImage } from '@/utils/imageUtils';
import { formatCurrency } from '@/utils/formatters';
import {
  ChartBarIcon,
  MagnifyingGlassIcon, 
  FunnelIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

interface InventoryItem {
  id: number;
  product_id: number;
  product: {
    id: number;
    name: string;
    price: number;
    images: Array<{ id: number; image_url: string }>;
  };
  current_stock: number;
  reserved_stock: number;
  reorder_point: number;
  is_in_stock: boolean;
  is_low_stock: boolean;
  updated_at: string;
}

interface ProductWithoutInventory {
  id: number;
  name: string;
  price: number;
  images?: any;
}

const InventoryPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [inventory, setInventory] = useState<InventoryItem[]>([]);
  const [filteredInventory, setFilteredInventory] = useState<InventoryItem[]>([]);
  const [productsWithoutInventory, setProductsWithoutInventory] = useState<ProductWithoutInventory[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchInventory();
    fetchProductsWithoutInventory();
  }, []);

  useEffect(() => {
    // Filter inventory based on search query
    if (!searchQuery) {
      setFilteredInventory(inventory);
    } else {
      const searchLower = searchQuery.toLowerCase();
      const filtered = inventory.filter((item) =>
        item.product?.name?.toLowerCase().includes(searchLower) ?? false
      );
      setFilteredInventory(filtered);
    }
  }, [inventory, searchQuery]);

  const fetchInventory = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Fetch real inventory data from API
      const response = await inventoryApi.getMyInventory();
      const inventoryData = response.data || [];
      setInventory(inventoryData);
    } catch (error: any) {
      // Safely extract error message
      let errorMessage = 'Failed to fetch inventory';
      if (error?.detail) {
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          errorMessage = error.detail.map((err: any) => err.msg || err.message || String(err)).join(', ');
        } else {
          errorMessage = String(error.detail);
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
      console.error('Inventory fetch error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProductsWithoutInventory = async () => {
    try {
      setIsLoadingProducts(true);
      const response = await inventoryApi.getProductsWithoutInventory();
      setProductsWithoutInventory(response.data || []);
    } catch (error: any) {
      console.error('Error fetching products without inventory:', error);
      // Don't show error to user, just log it
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleBulkAddToInventory = async () => {
    if (productsWithoutInventory.length === 0) return;
    
    try {
      const productIds = productsWithoutInventory.map(p => p.id);
      await inventoryApi.bulkCreate(productIds);
      toast.success(`Added ${productIds.length} product(s) to inventory`);
      // Refresh both lists
      await fetchInventory();
      await fetchProductsWithoutInventory();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to add products to inventory');
    }
  };

  const handleAddSingleToInventory = async (productId: number) => {
    try {
      await inventoryApi.create(productId, {
        current_stock: 0,
        reorder_point: 0,
      });
      toast.success('Product added to inventory');
      // Refresh both lists
      await fetchInventory();
      await fetchProductsWithoutInventory();
    } catch (error: any) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already exists')) {
        toast.error('Product already has inventory');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to add product to inventory');
      }
    }
  };

  const handleUpdateStock = async (itemId: number, newStockValue: number) => {
    try {
      await inventoryApi.setStock(itemId, newStockValue);
      // Refresh inventory after update
      await fetchInventory();
    } catch (error: any) {
      console.error('Failed to update stock:', error);
      alert(error.response?.data?.detail || 'Failed to update stock');
    }
  };

  const getStockStatus = (item: InventoryItem) => {
    if (item.current_stock === 0) {
      return { color: 'text-red-600', icon: XCircleIcon, text: 'Out of Stock' };
    } else if (item.is_low_stock) {
      return { color: 'text-yellow-600', icon: ExclamationTriangleIcon, text: 'Low Stock' };
    } else {
      return { color: 'text-green-600', icon: CheckCircleIcon, text: 'In Stock' };
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading inventory...</p>
        </div>
      </div>
    );
  }

  if (error) {
    // Safely convert error to string
    const errorMessage = typeof error === 'string' ? error : error?.toString() || 'An error occurred';
    
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error loading inventory</h3>
          <p className="text-gray-600 mb-6">{errorMessage}</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={fetchInventory}>Try Again</Button>
            {errorMessage.includes('Shop not found') && (
              <Button variant="outline" onClick={() => navigate('/dashboard/seller/shop/create')}>
                Create Shop
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Inventory Management</h1>
            <p className="mt-2 text-gray-600">Manage your product stock levels</p>
          </div>
          <Button onClick={() => navigate('/dashboard/seller/products/add')}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ChartBarIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Products</p>
              <p className="text-2xl font-semibold text-gray-900">{inventory.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">In Stock</p>
              <p className="text-2xl font-semibold text-gray-900">
                {inventory.filter(item => item.is_in_stock && !item.is_low_stock).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Low Stock</p>
              <p className="text-2xl font-semibold text-gray-900">
                {inventory.filter(item => item.is_low_stock).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <XCircleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Out of Stock</p>
              <p className="text-2xl font-semibold text-gray-900">
                {inventory.filter(item => !item.is_in_stock).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Products Without Inventory Section */}
      {productsWithoutInventory.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Products Without Inventory ({productsWithoutInventory.length})
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                These products need to be added to inventory to manage stock levels.
              </p>
            </div>
            <Button onClick={handleBulkAddToInventory} className="bg-yellow-600 hover:bg-yellow-700">
              <PlusIcon className="h-5 w-5 mr-2" />
              Add All to Inventory
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {productsWithoutInventory.map((product) => (
              <div key={product.id} className="bg-white rounded-lg border p-4 flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{product.name}</p>
                  <p className="text-xs text-gray-500">{formatCurrency(product.price || 0)}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleAddSingleToInventory(product.id)}
                  className="ml-2"
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
          <Button variant="outline">
            <FunnelIcon className="h-5 w-5 mr-2" />
            Filter
          </Button>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Current Stock
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reserved
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reorder Point
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredInventory.length === 0 && inventory.length > 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500 mb-2" />
                      <p>Inventory items found but product data is missing.</p>
                      <p className="text-sm mt-1">Please refresh the page or contact support.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredInventory.map((item) => {
                  // Skip items with missing product data
                  if (!item.product) {
                    return null;
                  }
                  
                  const status = getStockStatus(item);
                  const StatusIcon = status.icon;
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <img
                            src={getPrimaryImage(item.product?.images)}
                            alt={item.product?.name || 'Product'}
                            className="h-12 w-12 object-cover rounded-lg"
                          />
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {item.product?.name || 'Unknown Product'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatCurrency(item.product?.price || 0)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.current_stock}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.reserved_stock}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{item.reorder_point}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <StatusIcon className={`h-5 w-5 mr-2 ${status.color}`} />
                          <span className={`text-sm ${status.color}`}>{status.text}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newStock = prompt('Enter new stock level:', item.current_stock.toString());
                              if (newStock !== null) {
                                const stock = parseInt(newStock);
                                if (!isNaN(stock) && stock >= 0) {
                                  handleUpdateStock(item.id, stock);
                                }
                              }
                            }}
                          >
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {filteredInventory.length === 0 && (
          <div className="text-center py-12">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No inventory found</h3>
            <p className="text-gray-600 mb-4">
              {searchQuery ? 'Try adjusting your search terms.' : 'Products are automatically added to inventory when created. If you have products without inventory, they will appear here once inventory is created.'}
            </p>
            {!searchQuery && (
              <Button onClick={() => navigate('/dashboard/seller/products')}>
                View My Products
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryPage;